<template>
  <span>
    <div class="page-level-functions">
      <action-button
        variant="success float-right ml-3"
        text="Guardar"
        i="fas fa-save"
        @click="saveClicked"
      />
      <b-dropdown
        text="Opciones"
        variant="outline-success"
        class="float-right ml-3"
      >
        <template #button-content>
          <em class="fas fa-list-alt mr-2" />
          Opciones
        </template>
        <b-dropdown-item class="ddl-item" @click="confirmUnblock">
          <em class="fas fa-unlock mr-2" style="color: #2db782" />
          Desbloquear
        </b-dropdown-item>
        <b-dropdown-item
          class="ddl-item"
          v-if="inEditMode"
          @click="handleActivateUser"
        >
          <em class="fas fa-redo mr-2" style="color: #2db782" />
          Reenviar Notificación
        </b-dropdown-item>
        <b-dropdown-item
          class="ddl-item"
          v-if="canDeleteUser"
          @click="confirmDeleteUser"
        >
          <em class="fas fa-times-circle mr-2" style="color: #c9545e" />
          Eliminar usuario
        </b-dropdown-item>
        <b-dropdown-item
          v-if="isSpecialist"
          class="ddl-item"
          @click="onConsolidateUsersPanel"
        >
          <em class="fas fa-user-friends mr-2" style="color: #2db782" />
          Consolidar
        </b-dropdown-item>
      </b-dropdown>

      <router-link to="/users">
        <action-button
          variant="outline-success float-right ml-3 mt-2 mt-md-0 mt-sm-2"
          text="Regresar"
          i="fas fa-arrow-circle-left"
        ></action-button>
      </router-link>
    </div>

    <custom-modal
      ref="confirmModal"
      title="Confirmación"
      save-text="Sí"
      cancel-text="No"
      icon="fa-bell"
      @save="unblockUser"
    >
      <div
        style="
          padding: 16px 16px 16px 16px;
          margin: 16px auto;
          background-color: #f2f2f2;
        "
      >
        {{ confirmationText }}
      </div>
    </custom-modal>

    <hr class="page-level-functions-separator" />

    <div v-if="showConsolidateUserAccount">
      <custom-callout
        :has-toggle="false"
        border-color="green"
        class="mb-4 custom-callout-invoice shadow"
      >
        <div class="row">
          <div class="col-md-12">
            <icon-view
              icon="fa fa-user-friends"
              icon-background="#fff"
              icon-color="#2db782"
              icon-size="16px"
              title="Consolidar"
            />
          </div>
        </div>

        <validation-observer
          ref="consolidateUserAccountRef"
          novalidate
          tag="form"
        >
          <div class="row ml-2 mt-3 mb-3 mr-2">
            <div class="col-md-4 col-sm-12 border-right border-right--variant">
              <div class="text-gray font-14 mb-2">Nombre de Usuario:</div>
              <validation-provider :rules="'required'" v-slot="{ errors }">
                <custom-input
                  v-model="consolidateModel.userNameSearch"
                  custom-error-msg="Favor de entrar el nombre de usuario."
                  :error="errors[0]"
                />
              </validation-provider>
            </div>
            <div class="col-md-5 col-sm-12">
              <div class="result-pill">Resultados</div>
              <div v-if="consolidateModel.userId">
                <span class="text-gray">SSN:</span>
                <span>{{ consolidateModel.socialSecurityNumber }}</span>
                <br />
                <span class="text-gray">Nombre:</span>
                <span>{{ consolidateModel.fullName }}</span>
                <br />
                <span class="text-gray">Rol:</span>
                <span>{{ consolidateModel.roleNames }}</span>
                <br />
                <span class="text-gray">Usuario:</span>
                <span>{{ consolidateModel.userName }}</span>
                <br />
              </div>
              <div v-else class="not-found-result">
                No se encontr&oacute; nombre de usuario en sistema
              </div>
            </div>
            <div class="col-md-3 text-right mt-4">
              <action-button
                class="w-45"
                i="fas fa-times-circle"
                variant="outline-danger btn-cancel danger m-2 pl-4 pr-4"
                @click="onConsolidateUsersPanel"
              >
                Cancelar
              </action-button>
              <action-button
                :i="
                  consolidateModel.userId
                    ? 'fas fa-arrow-circle-right'
                    : 'fas fa-search'
                "
                variant="success pl-4 pr-4"
                @click="consolidateButtonAction"
              >
                {{ consolidateButtonText }}
              </action-button>
            </div>
          </div>
          <div class="ml-4" />
        </validation-observer>
      </custom-callout>
    </div>

    <div class="sub-content-wrapper" style="margin-bottom: 55px">
      <div class="row">
        <div class="col-md-5 col-sm-12 p-0 mb-3">
          <section-title
            :content="pageTitle"
            icon="fas fa-user"
          ></section-title>
        </div>
        <div class="col-md-7 col-sm-12 p-0" v-if="inEditMode">
          <user-summary-bar :user="userData" />
        </div>
      </div>

      <custom-tab class="user-tabs" v-model="tabIndex">
        <!-- general info tab start -->
        <custom-tab-item lazy title="Información General" active>
          <general-tab
            ref="generalTab"
            v-model="userData"
            :in-edit-mode="inEditMode"
            @status-change="handleStatusChange"
          />
        </custom-tab-item>
        <!-- permissions tab start -->
        <custom-tab-item
          lazy
          title="Permisos"
          v-if="userHasPermissions('security.tab.permission')"
        >
          <permission-selection
            ref="permissionSelectionRef"
            :user-id="userId"
            @on-select-permission="onSelectPermissionHandler"
          />
        </custom-tab-item>
        <custom-tab-item
          lazy
          title="Actividad de la Cuenta"
          v-if="userHasPermissions('security.user.view.activity')"
        >
          <user-activity-history :user-id="userId" />
        </custom-tab-item>
      </custom-tab>
    </div>
  </span>
</template>

<script>
import CustomTab from 'custom-tabs/src/CustomTab';
import CustomTabItem from 'custom-tabs/src/CustomTabItem';
import UserSummaryBar from './components/UserSummaryBar';
import GeneralTab from './components/UserGeneralTab';
import ActionButton from '@/components/common/ActionButton';
import SectionTitle from '@/components/common/SectionTitle';
import CustomModal from '@/components/common/CustomModal';
import CustomCallout from '@/components/common/CustomCallout';
import IconView from '@/components/common/IconView';
import PermissionSelection from '@/views/shared/PermissionSelection.vue';
import UserActivityHistory from './components/UserActivityHistory.vue';
import { name as appsModuleName } from '@/modules/users/store/';
import {
  addNewUser,
  activateUser,
  getUser,
  updateUser,
  validateUserHasDigitalSignature,
  unlockUser,
  resendActivationNotification,
  deleteUser,
  updateUserPermissions,
  getUsersWithSameEmail,
  getByUserName,
  consolidateUserAccount,
  createDESpecialist,
} from '@/modules/users/api';

import alert from '@/mixins/alert.mixin';
import { mapActions } from 'vuex';
import loading from '@/mixins/vue-loading';
import CustomInput from 'CustomInput';
import roles from '@/utils/constants/roles.js';
import rolesIds from '@/utils/constants/rolesIds';
import ApplicationIds from '@/utils/constants/applicationIds';
import {getSysParamByName} from '@/modules/common/api';

const USER_GENERAL_INFO_TAB_INDEX = 0;
const USER_PERMISSION_TAB_INDEX = 1;
const ACTIONS = {
  Deleted: 'delete',
  Addorupdate: 'addorupdate',
};
export default {
  name: 'ManageUser',
  components: {
    ActionButton,
    SectionTitle,
    UserSummaryBar,
    GeneralTab,
    CustomModal,
    CustomTab,
    CustomTabItem,
    PermissionSelection,
    CustomCallout,
    IconView,
    CustomInput,
    UserActivityHistory,
  },
  mixins: [loading, alert],
  props: {
    userId: {
      type: String,
      default: () => null,
    },
  },
  data() {
    return {
      userData: {
        firstName: null,
        middleInitial: null,
        paternalLastName: null,
        maternalLastName: null,
        socialSecurityNumber: null,
        email: null,
        userName: null,
        activeFlag: true,
        authenticationMode: '1',
        roles: [],
        userLocations: [],
        profiles: [],
        applicationIds: '',
        permissions: [],
        operation: null,
        sifde: null,
        parentPortalInfo: {
          relationship: null,
          physicalAddressLine1: null,
          physicalAddressLine2: null,
          physicalAddressMunicipality: null,
          physicalAddressPostalCode: null,
          postalAddressLine1: null,
          postalAddressLine2: null,
          postalAddressMunicipality: null,
          postalAddressPostalCode: null,
          email: null,
          phone: null,
          provider: null,
        },
        sipaeeInfo: {
          contracts: [],
        },
      },
      consolidateModel: {
        userName: null,
        userId: null,
        fullName: null,
        socialSecurityNumber: null,
        roleNames: null,
        userNameSearch: null,
        roleIds: [],
      },
      createDeSpecialistParam: false,
      confirmationText: '',
      permissions: [],
      originalRoles: [],
      userIdValue: this.userId,
      tabIndex: 0,
      showConsolidateUserAccount: false,
      isStatusChange: false,
    };
  },

  computed: {
    canDeleteUser() {
      return (
        !this.userData.isApproved && this.userData.activeFlag && this.inEditMode
      );
    },
    inEditMode() {
      return !!this.userId || !!this.userIdValue;
    },
    pageTitle() {
      return this.inEditMode
        ? 'Información del Usuario'
        : 'Añadir Información del Usuario';
    },
    consolidateButtonText() {
      return this.consolidateModel.userId ? 'Continuar' : 'Buscar';
    },
    isSpecialist() {
      return this.userData.roles?.some(
        (x) => x.roleId == roles.especialist || x.roleId == roles.especialist_RP
      );
    },
    userToConsolidateIsSpecialist() {
      return this.consolidateModel?.roleIds?.some(
        (roleId) =>
          roleId == roles.especialist || roleId == roles.especialist_RP
      );
    },
  },
  watch: {
    //ECG:Remuevo mascara en modelo interno
    'userData.socialSecurityNumber': function(newVal) {
      this.userData.socialSecurityNumber = newVal.replace('-', '');
    },
  },
  async created() {
    await this.getUserEdit();
    const { data } = await getSysParamByName({
      parameterNames: [
        'EnableDESpecialistExemptionNewFlow'
      ],
    });
    this.createDeSpecialistParam = data[0].staticValue.toLowerCase() === 'true';
  },
  methods: {
    async getUserEdit() {
      if (this.inEditMode) {
        this.ShowWait();

        const userIdToRequest =
          this.userId == null ? this.userIdValue : this.userId;

        await getUser(userIdToRequest).then((data) => {
          this.userData = {
            ...data,
            profiles: [],
          };
        });

        this.HideWait();
      }
    },
    findArrayDifference(array1, array2) {
      const addedOrUpdatedItems = [];
      const deletedItems = [];

      for (const newItem of array2) {
        const matchingItem = array1.find(
          (oldItem) =>
            oldItem.roleId === newItem.roleId &&
            oldItem.applicationId === newItem.applicationId
        );

        if (!matchingItem) {
          newItem.action = ACTIONS.Addorupdate;
          addedOrUpdatedItems.push(newItem);
        }
      }

      for (const oldItem of array1) {
        const matchingItem = array2.find(
          (newItem) =>
            newItem.roleId === oldItem.roleId &&
            newItem.applicationId === oldItem.applicationId
        );

        if (!matchingItem) {
          oldItem.action = ACTIONS.Deleted;
          deletedItems.push(oldItem);
        }
      }
      let combinedData = deletedItems.concat(addedOrUpdatedItems);

      let groupedData = {};

      combinedData.forEach(function(item) {
        let applicationId = item.applicationId;

        if (!groupedData[applicationId]) {
          groupedData[applicationId] = [];
        }

        groupedData[applicationId].push(item);
      });

      var resultArray = Object.values(groupedData);
      return resultArray;
    },
    confirmDeleteUser() {
      this.setupConfirmationModal(
        `¿Está seguro que desea eliminar el usuario ${this.userData.userName} ?`,
        this.delete
      );
      this.$refs.confirmModal.show();
    },
    async delete() {
      const userId = this.userData.userId;

      await deleteUser(userId).then((res) => {
        if (res.status === 200) {
          this.Toast(
            'Exito',
            'El usuario ha sido eliminado exitosamente.',
            'success'
          );
          setTimeout(() => this.$router.go(-1), 500);
        } else {
          this.Toast(
            'Error',
            'Ocurrió un error eliminando al usuario.',
            'error'
          );
        }
      });
    },
    orderDataRoles(listRoles) {
      const message = [];
      for (const arrayInterno of listRoles) {
        if (
          arrayInterno.length === 1 &&
          arrayInterno[0].action === ACTIONS.Deleted
        ) {
          message.push(
            `Eliminar Rol en Aplicación: ${arrayInterno[0].applicationDisplayName} - ${arrayInterno[0].roleName}`
          );
        } else if (
          arrayInterno.length === 2 &&
          arrayInterno.some((obj) => obj.action === ACTIONS.Addorupdate)
        ) {
          message.push(
            `Cambio de Rol en Aplicación: ${arrayInterno[0].applicationDisplayName} - ${arrayInterno[0].roleName} => ${arrayInterno[1].roleName}`
          );
        } else if (
          arrayInterno.length === 1 &&
          arrayInterno[0].action === ACTIONS.Addorupdate
        ) {
          message.push(
            `Añadir Aplicación y Rol Nuevo: ${arrayInterno[0].applicationDisplayName} - ${arrayInterno[0].roleName}`
          );
        }
      }
      // Agrupar  "Eliminados"
      const deleted = message
        .filter((mensaje) => mensaje.includes('Eliminar Rol en Aplicación:'))
        .map((mensaje) => mensaje.replace('Eliminar Rol en Aplicación: ', ''))
        .join(', ');

      // Agrupar  "añadidos"
      const added = message
        .filter((mensaje) =>
          mensaje.includes('Añadir Aplicación y Rol Nuevo: ')
        )
        .map((mensaje) =>
          mensaje.replace('Añadir Aplicación y Rol Nuevo: ', '')
        )
        .join(', ');

      // Agrupar  "Actualizados"
      const updated = message
        .filter((mensaje) => mensaje.includes('Cambio de Rol en Aplicación:'))
        .map((mensaje) => mensaje.replace('Cambio de Rol en Aplicación: ', ''))
        .join(', ');

      return {
        deleted: deleted,
        added: added,
        updated: updated,
      };
    },
    async saveClicked() {
      var self = this;
      let passed = true;
      let isPositionFormValid = true;
      const userIdToRequest =
        this.userId == null ? this.userIdValue : this.userId;

      if (userIdToRequest != null) {
        let oldData = await getUser(userIdToRequest);
        let actionByRoles = this.orderDataRoles(
          this.findArrayDifference(oldData.roles, this.userData.roles)
        );
        this.userData.actionsRoles = actionByRoles;
      }

      if (
        this.userData.roles.some(
          (c) => c.roleId == rolesIds.Maestro.toLowerCase()
        ) &&
        self.$refs.generalTab.$refs.roleAssignment?.academicProfile
          ?.userLocations.length > 1
      ) {
        self.Toast(
          'Error',
          'Usuario con rol Maestro no puede tener mas de una escuela asignada.',
          'error'
        );
        return;
      }

      if (!this.isStatusChange) {
        const roleSectionForm = this.$refs?.generalTab?.$refs?.roleAssignment;
        // JS - US 37569: if the position section is shown then validate the position's form. otherwise ignore its validation.
        const canSetPosition =
          roleSectionForm?.canSetProfileInfo && roleSectionForm?.canSetPosition;
        if (this.tabIndex === USER_GENERAL_INFO_TAB_INDEX && canSetPosition) {
          isPositionFormValid =
            await this.$refs.generalTab?.$refs.roleAssignment?.$refs.positionAssigment?.$refs.profileVO.validate();
        }

        if (this.tabIndex !== USER_PERMISSION_TAB_INDEX) {
          passed = await this.$refs.generalTab.validate();
        }
      }


      let operationPromise = null;
      let operation = null;

      if (passed && isPositionFormValid) {
        if (this.tabIndex === USER_GENERAL_INFO_TAB_INDEX)
          this.userData.staffId = this.$refs.generalTab.$refs.roleAssignment.$refs.positionAssigment?.getModel()?.staffId;

        if (this.userData.email) {
          const { data } = await getUsersWithSameEmail(
            this.userData.userId ?? null,
            this.userData.email
          );

          if (!this.userData.userId && data.length > 0) {
            self.Toast(
              'Error',
              'El correo electrónico ya esta siendo usado por otra cuenta de usuario.',
              'error'
            );
            return;
          } else if (this.userData.userId && data.length > 0) {
            const response = await this.ShowCancelModal(
              'Confirmación',
              `El correo electrónico existe en la(s) siguiente(s) cuenta(s) de usuario: [${data
                .map((x) => x.name)
                .join(
                  ', '
                )}], ¿Está seguro que desea eliminar el correo electrónico de esas cuentas?`
            );
            if (!response) return;
          }
        }

        if (self.inEditMode) {
          operation = 'U';
          if (this.tabIndex === USER_PERMISSION_TAB_INDEX) {
            operationPromise = updateUserPermissions(self.userData);
          } else {
            const { activeFlag } = self.userData;

            if (!activeFlag) {
              const {
                data: hasDocuments,
              } = await validateUserHasDigitalSignature(self.userData);

              if (hasDocuments) {
                const confirmed = await this.ShowCancelModal(
                  'Confirmación',
                  `<h5>Se identificó que el usuario tiene documentos asociados en DE-eSignature.</h5><p>¿Está seguro que desea inactivar la cuenta?</p>`,
                  'Sí',
                  'No'
                );
                if (!confirmed) return;
              }
            }

            operationPromise = updateUser(self.userData);
          }
        } else {
          operation = 'I';
          operationPromise = addNewUser(self.userData);
        }

        //pass operation to call insert or update function
        self.userData = {
          ...self.userData,
          operation,
        };

        if (operationPromise) {
          operationPromise
            .then(async (res) => {
              if (this.tabIndex !== USER_PERMISSION_TAB_INDEX) {
                if (res.data?.userId) {
                  self.userData.userId = res.data.userId;
                  this.userIdValue = res.data.userId;
                }
                let profilePromises = self.$refs.generalTab.saveProfiles();

                //execute promise sequentially
                const executePromiseSequentially = (arrayOrPromises) => {
                  var result = Promise.resolve();
                  arrayOrPromises.forEach(function(promiseFn) {
                    result = result.then(promiseFn);
                  });
                  return result;
                };
                await executePromiseSequentially(profilePromises);
              }
              const DE_TeacherRoles = [
                rolesIds.Maestro.toLowerCase(),
                rolesIds.MaestroRegular.toLowerCase(),
                rolesIds.MaestroItinerante.toLowerCase(),
                rolesIds.MaestroTransitorio.toLowerCase(),
              ];

              const hasTeacherRole = self.userData.roles.some(x =>
                DE_TeacherRoles.includes(x.roleId.toLowerCase())
              );

              const hasFinancePortalRole = self.userData.roles.some(x =>
                x.applicationId === ApplicationIds.financePortal
              );

              const userNeedsDefaultSpecialist =
                hasTeacherRole &&
                !hasFinancePortalRole &&
                this.createDeSpecialistParam;

              if(userNeedsDefaultSpecialist) {
                await createDESpecialist(self.userData.userId)
              }

              self.handleOperationSuccess(operation, self);
              await this.$refs.permissionSelectionRef?.resetTempPermissions();
              if (!this.userId) {
                await self.getUserEdit();

                self.$router.push({
                  name: 'edituser',
                  params: { userId: this.userIdValue },
                });
              }
            })
            .catch((err) => {
              if (!self.inEditMode) {
                self.userData.userId = null;
              }
              self.handleOperationErrors(err, operation, self);
            });
        }
      } else {
        self.AlertValidationError();
      }
    },
    async handleActivateUser() {
      var self = this;
      var res = await activateUser(self.userData);
      if (res.data != undefined && res.data != '' && res.data != '') {
        self.ShowAlertModal(
          'Confirmación',
          `La contraseña temporal asignada es: ${res.data}`
        );
        self.getUserEdit();
      }
    },
    handleOperationSuccess(op, self) {
      const verb = op === 'I' ? 'creado' : op == 'U' ? 'actualizado' : '???'; //ECG:No se que le hiciste al user! :P
      self.Toast(
        'Aviso',
        'El usuario fue ' + verb + ' exitosamente.',
        'success'
      );
    },
    handleOperationErrors(err, op, self) {
      let showValidationToast = false;

      if (err.response?.data?.length > 0 && err.response.status == 500) {
        showValidationToast = this.$refs.generalTab.setErrors(
          err.response.data
        );
      }

      let verb = op == 'I' ? 'creando' : op == 'U' ? 'actualizando' : '?';
      if (showValidationToast) {
        self.Toast(
          'Error',
          err.response?.data[0]?.message ??
            'Ocurrio un error ' + verb + ' el usuario.',
          'error'
        );
      } else {
        self.Toast(
          'Error',
          err.response?.data[0]?.message ??
            err.response?.data?.message ??
            'Ocurrio un error ' + verb + ' el usuario.',
          'error'
        );
      }
    },
    confirmUnblock() {
      this.setupConfirmationModal(
        '¿Está seguro que desea desbloquear la cuenta?',
        this.unblockUser
      );
      this.$refs.confirmModal.show();
    },

    async unblockUser() {
      var self = this;
      let result = await unlockUser(this.userData.userName);
      if (result) {
        self.Toast(
          'Exito',
          'La cuenta de usuario fue desbloqueada exitosamente.',
          'success'
        );
        this.userData.isLockedOut = false;
      } else {
        self.Toast(
          'Error',
          'Ocurrio un error desbloqueando la cuenta de usuario.',
          'error'
        );
      }
    },
    confirmMailResend() {
      this.setupConfirmationModal(
        '¿Está seguro que desea reenviar el correo de aprobación? Este proceso reiniciará y obligará al usuario a cambiar su contraseña.',
        this.resendConfirmationMail
      );
      this.$refs.confirmModal.show();
    },
    async resendConfirmationMail() {
      var self = this;

      let result = await resendActivationNotification(
        this.userData.userId,
        this.userData.applicationIds
      );
      if (result) {
        self.Toast(
          'Exito',
          'El correo de activación fue enviado exitosamente.',
          'success'
        );
      } else {
        self.Toast(
          'Error',
          'Ocurrio un error enviando el correo de activación.',
          'error'
        );
      }
    },
    setupConfirmationModal(text, onSaveMethod) {
      this.$refs.confirmModal.$off('save');
      this.$refs.confirmModal.$on('save', onSaveMethod);
      this.confirmationText = text;
    },
    onSelectPermissionHandler(permissions) {
      this.userData = {
        ...this.userData,
        permissions,
      };
    },
    ...mapActions(appsModuleName, ['setAffectedUserData']),
    onConsolidateUsersPanel() {
      if (!this.showConsolidateUserAccount) {
        this.showConsolidateUserAccount = true;
        this.consolidateModel = {
          ...this.consolidateModel,
          userName: null,
          userId: null,
          userNameSearch: null,
        };
      } else {
        this.showConsolidateUserAccount = false;
      }
    },
    async consolidateButtonAction() {
      if (!this.consolidateModel.userId) {
        await this.getUserToConsolidate();
        return;
      }

      if (this.userData.userId == this.consolidateModel.userId) {
        this.Toast(
          'Error',
          'No es posible consolidar el mismo usuario del perfil del usuario a consolidar.',
          'error'
        );
      } else if (!this.userToConsolidateIsSpecialist) {
        this.Toast(
          'Error',
          'Para consolidar cuentas es requerido que los usuarios tengan el mismo rol asignado.',
          'error'
        );
      } else {
        const response = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro que desea consolidar el usuario ${this.consolidateModel.userName} en ${this.userData.userName}? Esta acción eliminará de forma permanente el usuario ${this.consolidateModel.userName}`
        );
        if (!response) return;
        await this.consolidateAction();
      }

      this.onConsolidateUsersPanel();
    },
    async getUserToConsolidate() {
      const isValid = await this.$refs.consolidateUserAccountRef.validate();
      if (isValid) {
        this.ShowWait();

        await getByUserName(this.consolidateModel.userNameSearch).then(
          (data) => {
            this.consolidateModel = {
              ...data,
              userNameSearch: this.consolidateModel.userNameSearch,
            };

            this.HideWait();
            return;
          }
        );

        this.HideWait();
      }
    },
    async consolidateAction() {
      const { data: success } = await consolidateUserAccount(
        this.userData.userId,
        this.consolidateModel.userId
      );
      if (!success) {
        this.Toast(
          'Error',
          'Ocurrio un error consolidando las cuentas de usuario.',
          'error'
        );
        return;
      }

      this.Toast(
        'Exito',
        'Se han consolidado las cuentas exitosamente.',
        'success'
      );
    },
    handleStatusChange(status) {
      this.isStatusChange = status;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/sass/config/_colors.scss';

.inline-grey-radio >>> div.custom-radio {
  background-color: #f2f2f2;
  font: 1rem 'Lato';
  letter-spacing: 0;
  color: #727272;
  font-weight: normal;
  margin-right: 0;
  padding: 7px 11px 7px 28px;
}

.inline-grey-radio >>> div.custom-radio input {
  position: static;
}

.inline-grey-radio >>> div.col-12:nth-child(2) {
  padding: 0;
}

.custom-tab.user-tabs::v-deep {
  border-bottom: unset;

  .custom-tab-container {
    --tab-active-color: #{map-get($colors, 'orange')};
    margin: 0;

    .nav-item {
      .nav-link {
        padding: 0.5rem 3rem;
        font-size: 1rem;
      }
    }
  }

  .tab-content {
    margin-top: 1rem;
  }
}

.result-pill {
  background-color: map-get($colors, 'gray-2');
  padding: 0.25rem 1.5rem;
  border-radius: 50rem;
  color: map-get($colors, 'white');
  margin: 0 auto 1.5rem;
  font-size: 0.9375rem;
  width: fit-content;
}

.not-found-result {
  font-weight: normal;
  color: map-get($colors, 'gray-3');
  text-align: center;
}
</style>
